import {
  BackofficePrestation,
  ExhibitorProfilesPackNumber,
  ExhibitorProfilesPackPrestationType,
  isExhibitorProfilesPackNumber,
  isExhibitorProfilesPackPrestationType,
  isPromotionPrestationDataType,
  PromotionPrestationDataType,
  SimplePrestation,
} from "@ac-expo/api-types";
import { BackofficePrestationType } from "@prisma/client";
import { getBuyableItemName } from "./buyable-items";

export const getPrestationName = (type: BackofficePrestationType) =>
  getBuyableItemName({ buyableType: type });

export const prestationsValues = Object.values(BackofficePrestationType).map((type) => ({
  value: type,
  label: getBuyableItemName({ buyableType: type }),
}));

export const getProPackNumber = (type: string): ExhibitorProfilesPackNumber => {
  const match = type.match(/exhibitorProfilesPack(\d+)/);
  if (!match) {
    throw new Error(`Unable to extract pack number from: ${type}`);
  }
  const res = parseInt(match[1], 10);
  if (isNaN(res) || !isExhibitorProfilesPackNumber(res)) {
    throw new Error(`Invalid pack number: ${res}`);
  }
  return res;
};

export const getExhibitorProfilesPackFromNumber = (
  number: ExhibitorProfilesPackNumber,
): ExhibitorProfilesPackPrestationType => {
  return `exhibitorProfilesPack${number}Credits`;
};

export const getExhibitorProfilesPackNumberFromPrestationType = (
  prestationType: ExhibitorProfilesPackPrestationType,
): ExhibitorProfilesPackNumber => {
  const match = prestationType.match(/exhibitorProfilesPack(\d+)/);
  if (!match) {
    throw new Error(`Unable to extract pack number from: ${prestationType}`);
  }
  const res = parseInt(match[1], 10);
  if (!isExhibitorProfilesPackNumber(res)) {
    throw new Error(`Invalid pack number: ${match[1]}`);
  }
  return res;
};

export const getExhibitorProfilesPackNumber = (
  prestationType?: string | null,
): ExhibitorProfilesPackNumber | null => {
  if (!prestationType || !isExhibitorProfilesPackPrestationType(prestationType)) return null;
  return getExhibitorProfilesPackNumberFromPrestationType(prestationType);
};

export const getPackLevel = (pack: ExhibitorProfilesPackPrestationType | null): number => {
  if (!pack) return 0;
  return getExhibitorProfilesPackNumberFromPrestationType(pack);
};

export const isPackDowngrade = (
  currentPack: ExhibitorProfilesPackPrestationType | null,
  newPack: ExhibitorProfilesPackPrestationType | null,
): boolean => {
  return getPackLevel(newPack) < getPackLevel(currentPack);
};

export const verifyPromotionPrestationOrThrow = (
  prestation: BackofficePrestation | SimplePrestation | undefined | null,
): BackofficePrestation & { data: PromotionPrestationDataType } => {
  if (prestation === null || prestation === undefined)
    throw new Error("Prestation 'promotion' not found");

  if (prestation.type !== "promotion") throw new Error("Prestation is not of type 'promotion'");

  if (!("data" in prestation)) throw new Error("Prestation 'promotion' has no data");

  const data = prestation.data;
  if (!isPromotionPrestationDataType(data))
    throw new Error("Prestation 'promotion' data is not a promotion data");

  return {
    ...prestation,
    data,
  } as BackofficePrestation & { data: PromotionPrestationDataType };
};
